import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef } from 'react';
import { Button, Dropdown, Space } from 'antd';
import {
	buildTokenMenu,
	flattenTokenNodes,
	parseTemplate,
	resolveLabelTokens,
	segmentsEqual,
	shiftOffset,
	TOKEN_MENU_CLASS,
} from './tokenTemplate';

// The pure template helpers live in tokenTemplate.js (shared with the Lexical
// formula editor, KPR-539); they are re-exported here so every existing
// import from TokenInput keeps working.
export {
	buildTokenMenu,
	flattenTokenNodes,
	formulaLiteralSpans,
	parseTemplate,
	resolveLabelTokens,
	segmentsEqual,
	serializeSegments,
	shiftOffset,
	TOKEN_MENU_CLASS,
	TOKEN_SUBMENU_CLASS,
} from './tokenTemplate';

/**
 * TokenInput — a template editor that shows `{{tokens}}` as removable pills.
 *
 * The one sanctioned deviation from antd-out-of-the-box (PR H decision table).
 * The stored value is still a plain template STRING — `"Molly: BP {{today}}"` —
 * exactly what the API's template renderer consumes. Nothing about the storage
 * format changes; only the way a human edits it does. A token is an opaque
 * unit: making it a non-editable pill means it can be moved or deleted but
 * never half-typed into `{{toda}}`, which is the failure this replaces.
 *
 * WHY contentEditable rather than an antd Input
 * ---------------------------------------------
 * Pills have to sit INLINE with the prose, wrap with it, and be deletable with
 * Backspace. No antd input renders arbitrary nodes between characters, and the
 * alternatives (an overlay mirror, a Select with tags) either desynchronize
 * from the caret or lose the surrounding text. So: one contentEditable region,
 * styled to match antd's input chrome (see `.token-input` in css/components.css).
 *
 * WHY the DOM sync is one-directional
 * -----------------------------------
 * The naive implementation re-renders the editable HTML on every keystroke,
 * which destroys the caret: the browser rebuilds the text node and the cursor
 * snaps to the start. So React NEVER owns the children of the editable region.
 * We write them imperatively, and only when the PARSED segments of the incoming
 * value differ from the parsed segments already in the DOM. Typing therefore
 * costs zero DOM rewrites — the DOM already says what the value says — while a
 * value replaced from outside (a form reset, a loaded rule) repaints properly.
 *
 * Public API — the integration pass builds against this:
 *   value        template string, e.g. 'Molly: BP {{today}}'
 *   onChange     (nextTemplateString) => void, fired on every edit
 *   tokens       the vocabulary offered by the dropdown and used for pill
 *                labels. Either a flat `[{token, label}]` list or a TREE of
 *                `{label, children:[...]}` groups (utils/automationTokens.js
 *                builds one) — a group opens the next tier of the menu rather
 *                than adding another line to one long list, and a node may be
 *                BOTH (a card-lookup is insertable AND walkable). `menuLabel`
 *                overrides the in-menu text where the pill's full path would
 *                only repeat its own trail. An unknown token still renders as a
 *                pill, labelled with its raw inner text.
 *   placeholder  shown when the value is empty
 *   ariaLabel    accessible name of the editable region (required for tests/AT)
 *   multiline    taller, wrapping region for message bodies (Enter allowed)
 *   disabled     optional; renders antd's disabled input chrome and stops edits
 *   describedBy  optional id(s) of the messages under the field (aria-describedby)
 *   invalid      optional; marks the region aria-invalid (a refusal under it)
 *   formula      optional; the value is a formula, so a `{{…}}` inside a text
 *                literal ("…") is text: it is never turned into a token, and
 *                it is drawn as text, never as a pill
 *
 * A pill's words follow the vocabulary: when `tokens` relabels a token the
 * value still holds (the chart builder's measure pills, when a measure is
 * renamed), the pill's label is rewritten in place — the caret untouched,
 * since the value itself did not change.
 *
 * A reference typed or pasted BY ITS LABEL — `{{Unit price}}` for the pill
 * that reads "Unit price" — becomes that token, when the label names exactly
 * one entry of the vocabulary and is not itself a token. A pill looks the
 * same whether it was chosen or typed, so it must mean the same (the chart
 * A8 review's I-2). A node may be `disabled` (listed, not insertable), with
 * its `menuLabel` saying why.
 *
 * A ref exposes `insertText(text, { caretBack })` — plain text at the caret,
 * with the caret optionally pulled back inside what was inserted (a function
 * chip inserts `round()` and parks the caret between the parens) — plus
 * `insertToken(token)` and `focus()`.
 *
 * The FORMULA editors no longer use it: the formula dialog, the chart
 * builder's calculations and quick add's calculated rows moved to the Lexical
 * editor (FormulaInput, KPR-539), whose caret steps over a pill the way a text
 * field steps over a character. The template surfaces (automations,
 * notifications, display templates) still edit here, and two read-only
 * formula views (CalculatedValueModal, DrivenRuleModal) still draw a disabled
 * one.
 */

const BLOCK_TAGS = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'SECTION']);

// ------------------------------------------------------------------ DOM I/O

/** A pill's words, rewritten in place when the vocabulary relabels its token. */
function relabelPill(pill, label) {
	const text = pill.querySelector('.token-input__pill-label');
	if (text && text.textContent !== label) text.textContent = label;
	const remove = pill.querySelector('[data-token-remove]');
	const name = `Remove ${label}`;
	if (remove && remove.getAttribute('aria-label') !== name) remove.setAttribute('aria-label', name);
}

function buildPill(doc, token, label) {
	const pill = doc.createElement('span');
	pill.className = 'token-input__pill';
	pill.setAttribute('contenteditable', 'false');
	// The verbatim source text lives on the node, so serializing back is exact
	// even for a token this editor does not recognize.
	pill.setAttribute('data-token', token);

	const text = doc.createElement('span');
	text.className = 'token-input__pill-label';
	text.textContent = label;
	pill.appendChild(text);

	const remove = doc.createElement('button');
	remove.setAttribute('type', 'button');
	remove.setAttribute('data-token-remove', '');
	remove.setAttribute('aria-label', `Remove ${label}`);
	remove.className = 'token-input__pill-remove';
	remove.textContent = '×';
	pill.appendChild(remove);

	return pill;
}

/** Segments -> a DocumentFragment of text nodes and pill elements. */
function segmentsToFragment(doc, segments, labelFor) {
	const fragment = doc.createDocumentFragment();
	(segments || []).forEach(segment => {
		if (segment.type === 'token') {
			fragment.appendChild(buildPill(doc, segment.token, labelFor(segment)));
			return;
		}
		// Newlines inside a text run become real <br>s; everything else is one
		// text node, so the caret can sit anywhere inside it.
		segment.text.split('\n').forEach((line, index) => {
			if (index > 0) fragment.appendChild(doc.createElement('br'));
			if (line) fragment.appendChild(doc.createTextNode(line));
		});
	});
	// A pill is contenteditable=false, so a trailing one leaves nowhere for the
	// caret to land. An empty text node after it is that landing spot.
	if (fragment.lastChild && fragment.lastChild.nodeType === 1
		&& fragment.lastChild.hasAttribute && fragment.lastChild.hasAttribute('data-token')) {
		fragment.appendChild(doc.createTextNode(''));
	}
	return fragment;
}

function readNode(node, out) {
	if (!node) return;
	if (node.nodeType === 3) {
		// contentEditable stores a trailing space as a non-breaking space; the
		// template must not carry U+00A0 the API never wrote.
		out.push(String(node.nodeValue || '').replace(/\u00a0/g, ' '));
		return;
	}
	if (node.nodeType !== 1) return;
	const token = node.getAttribute && node.getAttribute('data-token');
	if (token) { out.push(token); return; }
	if (node.tagName === 'BR') { out.push('\n'); return; }
	if (BLOCK_TAGS.has(node.tagName) && out.length && !out[out.length - 1].endsWith('\n')) {
		// The browser wraps a new line in a <div> after Enter.
		out.push('\n');
	}
	Array.from(node.childNodes).forEach(child => readNode(child, out));
}

/** The live DOM of the editable region -> the template string it represents. */
export function domToTemplate(root) {
	if (!root) return '';
	const children = Array.from(root.childNodes);
	// Browsers park a bogus trailing <br> in an otherwise-empty editable region.
	// It is chrome, not content.
	if (children.length && children[children.length - 1].nodeName === 'BR') children.pop();
	const out = [];
	children.forEach(child => readNode(child, out));
	return out.join('');
}

// -------------------------------------------------------------- selection

function currentSelection(win) {
	return win && typeof win.getSelection === 'function' ? win.getSelection() : null;
}

function applyRange(win, range) {
	const selection = currentSelection(win);
	if (!selection || !range) return;
	selection.removeAllRanges();
	selection.addRange(range);
}

/** How far into the template a caret at (container, offset) sits. */
function templateOffsetOf(root, container, offset) {
	const doc = root.ownerDocument;
	const range = doc.createRange();
	range.setStart(root, 0);
	range.setEnd(container, offset);
	const holder = doc.createElement('div');
	holder.appendChild(range.cloneContents());
	return domToTemplate(holder).length;
}

/** The caret at an offset into the template: in the text node it falls in, or just after the pill it ends. */
function caretToOffset(win, doc, element, target) {
	let count = 0;
	const nodes = Array.from(element.childNodes);
	for (let i = 0; i < nodes.length; i += 1) {
		const node = nodes[i];
		if (node.nodeType === 3) {
			const length = String(node.nodeValue || '').length;
			if (target <= count + length) {
				const range = doc.createRange();
				range.setStart(node, Math.max(0, target - count));
				range.collapse(true);
				applyRange(win, range);
				return range;
			}
			count += length;
		} else {
			count += node.getAttribute && node.getAttribute('data-token') ? node.getAttribute('data-token').length : 1;
			if (target <= count) {
				const range = doc.createRange();
				range.setStartAfter(node);
				range.collapse(true);
				applyRange(win, range);
				return range;
			}
		}
	}
	caretToEnd(win, doc, element);
	return null;
}

function caretToEnd(win, doc, element) {
	if (!element) return;
	const range = doc.createRange();
	range.selectNodeContents(element);
	range.collapse(false);
	applyRange(win, range);
}

/** The node immediately before a collapsed caret, or null. */
function nodeBeforeCaret(range, root) {
	const { startContainer, startOffset } = range;
	if (startContainer === root) return root.childNodes[startOffset - 1] || null;
	if (startContainer.nodeType === 3) {
		if (startOffset > 0) return null; // there is a character to delete first
		return startContainer.previousSibling;
	}
	return startContainer.childNodes[startOffset - 1] || null;
}

const pillOf = node => (node && node.nodeType === 1 && node.getAttribute
	&& node.getAttribute('data-token') ? node : null);

// ------------------------------------------------------------- component

const TokenInput = forwardRef(function TokenInput({
	value = '',
	onChange,
	tokens = [],
	placeholder = '',
	ariaLabel = 'Template',
	multiline = false,
	disabled = false,
	describedBy,
	invalid = false,
	formula = false,
}, ref) {
	const editorRef = useRef(null);
	const rangeRef = useRef(null);
	// Where the caret belongs after a typed label became its pill (a template offset).
	const caretAfterRef = useRef(null);
	const valueRef = useRef(value);
	valueRef.current = value;

	const segments = useMemo(() => parseTemplate(value, { formula }), [value, formula]);

	// Pill labels come from EVERY node of the tree, at any depth: a token
	// inserted from the third tier has to render with the same words when the
	// rule is re-opened. The first definition of a token wins.
	const labelByToken = useMemo(() => {
		const map = new Map();
		flattenTokenNodes(tokens).forEach(entry => {
			if (!map.has(entry.token)) map.set(entry.token, entry.label || entry.token);
		});
		return map;
	}, [tokens]);

	// A label -> its token, for a reference typed by its label; null where two
	// tokens share a label (never guessed).
	const tokenByLabel = useMemo(() => {
		const map = new Map();
		flattenTokenNodes(tokens).forEach(entry => {
			const label = typeof entry.label === 'string' ? entry.label.trim() : '';
			if (!label) return;
			if (!map.has(label)) map.set(label, entry.token);
			else if (map.get(label) !== entry.token) map.set(label, null);
		});
		return map;
	}, [tokens]);

	// An unknown token still gets a readable pill: its own inner text. Silently
	// refusing to render it would hide data the rule actually stores.
	const labelFor = useCallback(
		segment => labelByToken.get(segment.token) || segment.inner || segment.token,
		[labelByToken]
	);

	/**
	 * DOM -> value. The ONLY place onChange is called, so every edit path
	 * (typing, paste, pill removal, token insert) emits the same way.
	 */
	const emit = useCallback(() => {
		const element = editorRef.current;
		if (!element) return;
		// A reference typed by its label becomes its token; the value then
		// differs from the DOM, so the sync below repaints it as that pill —
		// and puts the caret back where the person was, just after the pill.
		const { text: next, edits } = resolveLabelTokens(domToTemplate(element), labelByToken, tokenByLabel, { formula });
		if (edits.length && next !== valueRef.current) {
			const selection = currentSelection(element.ownerDocument.defaultView);
			const range = selection && selection.rangeCount ? selection.getRangeAt(0) : null;
			caretAfterRef.current = range && range.collapsed && element.contains(range.startContainer)
				? shiftOffset(templateOffsetOf(element, range.startContainer, range.startOffset), edits)
				: null;
		}
		if (next === valueRef.current) return;
		valueRef.current = next;
		if (typeof onChange === 'function') onChange(next);
	}, [onChange, labelByToken, tokenByLabel, formula]);

	// The unidirectional sync. Repaint only when the DOM and the value disagree
	// about the SEGMENTS — after a keystroke they agree, so the caret is never
	// disturbed by our own echo.
	useLayoutEffect(() => {
		const element = editorRef.current;
		if (!element) return;
		if (segmentsEqual(parseTemplate(domToTemplate(element), { formula }), segments)) {
			// Same value, maybe new words for a token (a measure renamed): the
			// pills follow in place, so the caret is never disturbed.
			element.querySelectorAll('[data-token]').forEach(pill => {
				const token = pill.getAttribute('data-token');
				relabelPill(pill, labelFor({ type: 'token', token, inner: token.slice(2, -2).trim() }));
			});
			return;
		}
		const doc = element.ownerDocument;
		const win = doc.defaultView;
		const hadFocus = doc.activeElement === element;
		while (element.firstChild) element.removeChild(element.firstChild);
		element.appendChild(segmentsToFragment(doc, segments, labelFor));
		const caretAfter = caretAfterRef.current;
		caretAfterRef.current = null;
		if (caretAfter !== null) {
			// A typed label just became its pill: the caret stays where it was.
			const range = caretToOffset(win, doc, element, caretAfter);
			rangeRef.current = range ? range.cloneRange() : null;
		} else if (hadFocus) caretToEnd(win, doc, element);
	}, [segments, labelFor, formula]);

	// Empty-state placeholder is a CSS ::before on a data attribute — a real
	// child node would end up inside the value.
	useEffect(() => {
		const element = editorRef.current;
		if (element) element.setAttribute('data-empty', segments.length ? 'false' : 'true');
	}, [segments]);

	const rememberSelection = useCallback(() => {
		const element = editorRef.current;
		if (!element) return;
		const selection = currentSelection(element.ownerDocument.defaultView);
		if (!selection || !selection.rangeCount) return;
		const range = selection.getRangeAt(0);
		if (element.contains(range.commonAncestorContainer)) rangeRef.current = range.cloneRange();
	}, []);

	/** Insert nodes at the remembered caret, or at the end when there is none. */
	const insertFragment = useCallback(fragment => {
		const element = editorRef.current;
		if (!element) return;
		const doc = element.ownerDocument;
		const win = doc.defaultView;
		const saved = rangeRef.current;
		const usable = saved && element.contains(saved.commonAncestorContainer) ? saved : null;
		const lastNode = fragment.lastChild;

		if (usable) {
			usable.deleteContents();
			usable.insertNode(fragment);
			if (lastNode) {
				const after = doc.createRange();
				after.setStartAfter(lastNode);
				after.collapse(true);
				applyRange(win, after);
				rangeRef.current = after.cloneRange();
			}
		} else {
			element.appendChild(fragment);
			caretToEnd(win, doc, element);
			rangeRef.current = null;
		}
		emit();
	}, [emit]);

	const insertToken = useCallback(token => {
		const element = editorRef.current;
		if (!element || disabled) return;
		const doc = element.ownerDocument;
		const fragment = doc.createDocumentFragment();
		const segment = { type: 'token', token, inner: token.slice(2, -2).trim() };
		fragment.appendChild(buildPill(doc, token, labelFor(segment)));
		fragment.appendChild(doc.createTextNode(''));
		insertFragment(fragment);
	}, [disabled, insertFragment, labelFor]);

	const insertText = useCallback((text, { caretBack = 0 } = {}) => {
		const element = editorRef.current;
		if (!element || disabled || typeof text !== 'string' || !text) return;
		const doc = element.ownerDocument;
		const node = doc.createTextNode(text);
		const fragment = doc.createDocumentFragment();
		fragment.appendChild(node);
		// Focusing fires onFocus -> rememberSelection, which would replace the
		// remembered caret with wherever the browser parks it on focus (the
		// start, in Chrome). Keep the caret the user last had.
		const remembered = rangeRef.current;
		if (typeof element.focus === 'function') element.focus();
		rangeRef.current = remembered;
		insertFragment(fragment);
		if (caretBack > 0 && node.parentNode) {
			const range = doc.createRange();
			range.setStart(node, Math.max(0, node.length - caretBack));
			range.collapse(true);
			applyRange(doc.defaultView, range);
			rangeRef.current = range.cloneRange();
		}
	}, [disabled, insertFragment]);

	useImperativeHandle(ref, () => ({
		insertText,
		insertToken,
		focus: () => { const element = editorRef.current; if (element && typeof element.focus === 'function') element.focus(); },
	}), [insertText, insertToken]);

	const handleClick = useCallback(event => {
		const trigger = event.target.closest && event.target.closest('[data-token-remove]');
		if (!trigger || disabled) return;
		event.preventDefault();
		const pill = trigger.closest('[data-token]');
		if (!pill) return;
		pill.remove();
		rangeRef.current = null;
		emit();
	}, [disabled, emit]);

	const handleKeyDown = useCallback(event => {
		if (disabled) { event.preventDefault(); return; }
		if (event.key === 'Enter' && !multiline) { event.preventDefault(); return; }

		if (event.key !== 'Backspace') return;
		const element = editorRef.current;
		const selection = currentSelection(element && element.ownerDocument.defaultView);
		if (!selection || !selection.rangeCount) return;
		const range = selection.getRangeAt(0);
		if (!range.collapsed || !element.contains(range.commonAncestorContainer)) return;
		// A pill is one unit: Backspace right after it removes the whole token
		// rather than dropping the caret inside a non-editable node.
		const pill = pillOf(nodeBeforeCaret(range, element));
		if (!pill) return;
		event.preventDefault();
		pill.remove();
		emit();
	}, [disabled, multiline, emit]);

	const handlePaste = useCallback(event => {
		if (disabled) { event.preventDefault(); return; }
		event.preventDefault();
		const text = event.clipboardData ? event.clipboardData.getData('text/plain') : '';
		if (!text) return;
		const element = editorRef.current;
		if (!element) return;
		rememberSelection();
		// Formatting is stripped by construction (we only ever read text/plain),
		// and any `{{token}}` in the pasted text becomes a pill immediately.
		const doc = element.ownerDocument;
		const pasted = multiline ? text : text.replace(/\r?\n/g, ' ');
		insertFragment(segmentsToFragment(doc, parseTemplate(pasted, { formula }), labelFor));
	}, [disabled, multiline, formula, insertFragment, labelFor, rememberSelection]);

	const { items: menuItems, tokenByKey } = useMemo(() => buildTokenMenu(tokens), [tokens]);

	const handleMenuClick = useCallback(({ key }) => {
		// Group titles never reach here (rc-menu fires onClick for leaves only),
		// but a group that is ALSO insertable does, and resolves to its token.
		const token = tokenByKey.get(key);
		if (token) insertToken(token);
	}, [tokenByKey, insertToken]);

	const className = [
		'token-input__editor',
		multiline ? 'token-input__editor--multiline' : '',
		disabled ? 'token-input__editor--disabled' : '',
	].filter(Boolean).join(' ');

	return (
		<Space.Compact
			className={`input-affix-group token-input${disabled ? ' token-input--disabled' : ''}`}
			style={{ width: '100%' }}
		>
			<div
				ref={editorRef}
				className={className}
				contentEditable={!disabled}
				suppressContentEditableWarning
				role="textbox"
				aria-label={ariaLabel}
				aria-multiline={multiline ? 'true' : 'false'}
				aria-disabled={disabled ? 'true' : undefined}
				aria-describedby={describedBy || undefined}
				aria-invalid={invalid ? 'true' : undefined}
				data-placeholder={placeholder}
				spellCheck={false}
				onInput={() => { rememberSelection(); emit(); }}
				onKeyUp={rememberSelection}
				onMouseUp={rememberSelection}
				onFocus={rememberSelection}
				onBlur={emit}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				onPaste={handlePaste}
			/>
			{/* Affix convention: the action sits rightmost, outside the editable
			    region — a button inside contentEditable is a caret trap. */}
			<Dropdown
				trigger={['click']}
				rootClassName={TOKEN_MENU_CLASS}
				disabled={disabled || !menuItems.length}
				menu={{
					items: menuItems,
					onClick: handleMenuClick,
					// Submenus open on CLICK, not hover: the tiers are the point of
					// the menu, and a hover-opened tier is unreachable by keyboard,
					// unusable on touch, and opens itself while the pointer is only
					// travelling past on the way to a sibling.
					triggerSubMenuAction: 'click',
				}}
			>
				<Button
					className="token-input__insert"
					disabled={disabled || !menuItems.length}
					aria-label={`Insert a token into ${ariaLabel}`}
					onMouseDown={event => event.preventDefault()}
				>
					{'{{ }}'}
				</Button>
			</Dropdown>
		</Space.Compact>
	);
});

export default TokenInput;
